import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"; import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../app/api";
import { SelectOptions } from "../components/Select/Select";
import { useTypedSelector } from "../hooks/useTypedSelector";
import useUrlQuery from "../hooks/useUrlQuery";
import { setProposalData } from "../modules/proposal.slice";
import { BikeResponse } from "./CarServices";
export interface addonsInterface {
  name: string;
  slug: string;
  premium: string | undefined;
}
interface InitiateBikeProposalRequest {
  PRODUCT_ID: number;
  BASIC_OD: string;
  BASIC_TP: string;
  NCB_DISCOUNT: string;
  GST: number;
  COMPANY_SLUG: string;
  TOTAL_PREMIUM: string;
  IDV: string;
  ADDONS: addonsInterface[];
  CORRELATION_ID: any;
}
export const useInitiateProposalMutation = () => {
  const { getUrlQuery, setUrlQuery } = useUrlQuery();
  const stage = getUrlQuery("stage");
  const quote_id = getUrlQuery("quote_id");
  const navigate = useNavigate();
  const { isPending, mutate } = useMutation(
    {
      mutationFn: (req: InitiateBikeProposalRequest) => {
        return api.post("/InitiateBikeProposal", {
          ...req,
          QUOTE_ID: quote_id,
        });
      },
      onSuccess: ({ data }: { data: BikeResponse & { response: { PROPOSAL_ID: string, session_token: string } } }) => {
        const proposal_id = data.response.PROPOSAL_ID;
        const token = data?.response?.session_token;

        if (token) {
          sessionStorage.setItem("session_token", token);
        }
        navigate(`/proposal?proposal_id=${proposal_id}&session_token=${token}`);
      },
    }
  );

  return { initiateProposal: mutate, isSubmitting: isPending };
};
interface PincodeRequest {
  pincode: string;
}
interface PincodeResponse extends BikeResponse {
  response: {
    city: SelectOptions[];
    state: [SelectOptions];
  };
}
interface ProposalDetailsResponse {
  data: {
    PROPOSAL: {
      _id: string;
      PRODUCT_ID: number;
      COMPANY_SLUG: string;
      TOTAL_PREMIUM: string;
      IDV: string;
      ADDONS: addonsInterface[];
      QUOTE_ID: string;
      FORMDATA?: any;
      BASIC_OD: string;
      BASIC_TP: string;
      NCB_DISCOUNT: string;
      GST: number;
    };
    QUOTE_REQUEST: {
      _id: string;
      vehicle_make: string;
      vehicle_model: string;
      vehicle_variant: string;
      vehicle_version_id: string;
      fuel_type: string;
      rto_number: string;
      lob: string;
      is_claim_made_last_year: string;
      is_policy_expired: string;
      mobile_no: string;
      existing_exp_date: string;
      registration_date: string;
      manf_month: string;
      prev_ncb: number;
      IDV?: string;
    };
    product_data: {
      PRODUCTID: number;
      PRODUCT_NAME: string;
      PRODUCT_CODE: string;
      BUSINESS_TYPE: string;
      LOB: string;
      STATUS: string;
      OWNER_TYPE: string;
      POLICY_TYPE: string;
      IC: number;
      SELLER_TYPE: string;
      IC_ID: number;
      INSURANCE_NAME: string;
      INSURANCE_SLUG: string;
      ADDRESS: string;
      CONTACT_DETAILS: string;
      CLAIM_FORM_LINK: string;
      BROCHURE_LINK: string;
      LOGO_PATH: string;
    };
  };
}
interface MasterResponse extends BikeResponse {
  response: SelectOptions[];
}
export const useGetOccupationQuery = () => {
  const { proposal_data } = useGetProposalDetailsQuery();
  const { data, isPending } = useQuery({
    queryKey: ["occupation", proposal_data?.COMPANY_SLUG],
    queryFn: () =>
      api.post<MasterResponse>("/GetOccupationMaster", {
        COMPANY_SLUG: proposal_data?.COMPANY_SLUG,
        LOB: "BIKE",
      }),
    enabled: !!proposal_data?.COMPANY_SLUG,
  });
  return {
    isOccupationLoading: isPending,
    occupation_list: data?.data.response,
  };
};
export const useGetPreviousInsurerQuery = () => {
  const { proposal_data } = useGetProposalDetailsQuery();
  const { isPending, data } = useQuery({
    queryKey: ["prev_insurer", proposal_data?.COMPANY_SLUG],
    queryFn: () => {
      return api.post<MasterResponse>("/GetPreviousInsurerMaster", {
        COMPANY_SLUG: proposal_data?.COMPANY_SLUG,
        LOB: "BIKE",
      });
    },
  });
  return {
    isInsurerLoading: isPending,
    insurer_list: data?.data.response,
  };
};
export const useGetRelationMasterQuery = () => {
  const { proposal_data } = useGetProposalDetailsQuery();
  const { isPending, data } = useQuery({
    queryKey: ["relation", proposal_data?.COMPANY_SLUG],
    queryFn: () => {
      return api.post<MasterResponse>("/GetRelationMaster", {
        LOB: "BIKE",
        COMPANY_SLUG: proposal_data?.COMPANY_SLUG,
      });
    },
    enabled: !!proposal_data?.COMPANY_SLUG,
  });

  return {
    relations: data?.data?.response,
    isRelationsLoading: isPending,
  };
};
export const useGetFinancierQuery = () => {
  const { proposal_data } = useGetProposalDetailsQuery();
  const { isPending, data } = useQuery({
    queryKey: ["financier", proposal_data?.COMPANY_SLUG],
    queryFn: () => {
      return api.post<MasterResponse>("/GetFinancierMaster", {
        COMPANY_SLUG: proposal_data?.COMPANY_SLUG,
        LOB: "BIKE",
      });
    },
  });
  return {
    isFinancierLoading: isPending,
    financier_list: data?.data.response,
  };
};
export const useGetProposalDetailsQuery = () => {
  const { getUrlQuery } = useUrlQuery();
  const [proposal_id] = useState(() => getUrlQuery("proposal_id"));
  const dispatch = useDispatch();
  const { isPending, data } = useQuery({
    queryKey: ["proposal_details", proposal_id],
    queryFn: () => {
      return api.post<ProposalDetailsResponse>("/GetBikeProposalDetails", {
        PROPOSAL_ID: proposal_id,
      });
    },
    enabled: !!proposal_id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  useEffect(() => {
    if (data?.data?.data?.PROPOSAL?.FORMDATA) {
      dispatch(setProposalData(data.data.data.PROPOSAL.FORMDATA));
    }
  }, [data, dispatch]);

  return {
    proposal_data: data?.data?.data?.PROPOSAL,
    quote_data: data?.data?.data?.QUOTE_REQUEST,
    product_data: data?.data?.data?.product_data,
    isProposalLoading: isPending,
  };
};
export const useUpdateProposalMutation: any = (options: any) => {
  const isFormData: boolean = options?.isFormData;
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const dispatch = useDispatch();
  const { activeForm } = useTypedSelector((state) => state.proposal);
  const { isPending, mutate, isSuccess, mutateAsync } = useMutation(
    {
      mutationFn: (req: any) => {
        const formData = new FormData();
        const newData = { PROPOSAL_ID: proposal_id, ...req };
        Object.keys(newData).forEach((item) => {
          formData.append(item, newData[item]);
        });
        dispatch(setProposalData(req));
        return api.post(
          "/UpdateBikeRawProposal",
          isFormData
            ? formData
            : {
              FORMDATA: req,
              PROPOSAL_ID: proposal_id,
            },
          {
            headers: {
              "Content-Type": isFormData
                ? "multipart/form-data"
                : "application/json",
            },
          }
        );
      },
      onSuccess: () => {
        toast.success("Data Saved Successfully");
      },
    }
  );
  return {
    updateProposal: mutate,
    isSubmitting: isPending,
    isSubmitted: isSuccess,
    asyncUpdateProposal: mutateAsync,
  };
};
export const useGetPincodeMutation = () => {
  const { proposal_data } = useGetProposalDetailsQuery();
  const { isPending, data, isError, mutate, isSuccess } = useMutation({
    mutationFn: ({ pincode }: PincodeRequest) => {
      return api.post<PincodeResponse>("/GetPincodeMaster", {
        pincode,
        company_alias: proposal_data?.COMPANY_SLUG,
      });
    },
  });
  return {
    isCityStateLoading: isPending,
    getCityState: mutate,
    cityOptions: data?.data?.response?.city,
    stateOptions: data?.data?.response?.state,
    isError,
    isCityStateFetched: isSuccess,
  };
};
export const serialize = function (obj: any) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
};
export const useProposalFinalSubmitMutation = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const session_token = getUrlQuery("session_token");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isPending, mutate, data } = useMutation({
    mutationFn: () => {
      return api.post("/BikeProposalFinalSubmit", {
        PROPOSAL_ID: proposal_id,
      });
    },

    onSuccess: (res: any) => {
      if (res.data.status === 500) {
        toast.error(res.data.message);
        return;
      }

      if (res.data.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["proposal_details"],
        });

        const params = new URLSearchParams({
          proposal_id: proposal_id || "",
          session_token: session_token || "",
        });

        navigate({
          pathname: "/summary",
          search: `?${params.toString()}`,
        });
      }
    },
  });

  return {
    isSubmitting: isPending,
    submitProposal: mutate,
    data: data?.data,
    apiStatus: data?.data?.status,
  };
};

export const usePaymentQuery = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");
  const session_token = getUrlQuery("session_token");
  const { proposal_data } = useGetProposalDetailsQuery();
  const navigate = useNavigate();

  const { isPending, data } = useQuery({
    queryKey: ["payment", proposal_id],

    queryFn: () => {
      return api.post("/GetBikePaymentDetails", {
        PROPOSAL_ID: proposal_id,
      });
    },

    enabled: !!proposal_id,
  });

  useEffect(() => {
    if (data?.data) {
      const res = data.data;

      if (res.status === 500 && !res.resubmit) {
        navigate({
          pathname: "/proposal",
          search: `?proposal_id=${proposal_id}&session_token=${session_token}`,
        });

        toast.error(res.message);
      }

      if (
        res.status === 200 &&
        proposal_data?.COMPANY_SLUG === "shri_ram"
      ) {
        const { fields, method, url } = res?.PG_FORM || {};

        if (method === "POST" && fields && url) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = url;
          form.target = "_blank";

          Object.keys(fields).forEach((key) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = fields[key];
            form.appendChild(input);
          });

          document.body.appendChild(form);
          form.submit();
        }
      }
    }
  }, [data, proposal_id, proposal_data, navigate, session_token]);

  return {
    paymentData: data?.data,
    isPaymentLoading: isPending,
  };
};
export const useUpdateOrderMutation = () => {
  const { getUrlQuery } = useUrlQuery();
  const proposal_id = getUrlQuery("proposal_id");

  const { isPending, mutate, isSuccess } = useMutation({
    mutationFn: () =>
      api.post("/UpdateOrderStage", {
        PROPOSAL_ID: proposal_id,
        ORDER_STAGE: "PG REDIRECTED",
      }),
  });

  return {
    updateOrder: mutate,
    isPending,
    isSuccess,
  };
};
export const useVerifyPanCKYC = () => {
  const { product_data } = useGetProposalDetailsQuery();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: (payload: any) => {
      return api.post(
        product_data?.INSURANCE_SLUG === "reliance"
          ? "/GetRelianceCKYCDetails"
          : "/GetHdfcCKYCDetails",
        { ...payload, LOB: "BIKE" }
      );
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["proposal_details"],
      });
    },
  });

  return {
    verifyCKYC: mutateAsync,
    isVerifyCKYC: isPending,
    resetVerify: reset,
  };
};

export const getMobileNumber = async (PROPOSAL_ID: string) => {
  const res = await api.post("/getBikeProposalOTP", {
    PROPOSAL_ID,
  });

  return res.data;
};

export const verifyOtpApi = async (
  PROPOSAL_ID: string,
  OTP: string
) => {
  const res = await api.post("/verifyBikeProposalOTP", {
    PROPOSAL_ID,
    OTP,
  });

  return res.data;
};
